import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Card, Dialog, Field, Input, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StorageLocationClient } from "../../api/locations";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import { AssemblyOrderClient, type ProductionOrderDTO } from "../../api/production";
import { ProductionStatusBadge } from "./badges";

/**
 * Assembly orders at Terrebonne (server: AssemblyOrderService): one per
 * version of a production order's product, firmed by the sets that arrive,
 * worked through their steps (Hardware kit, Foam, Cornières, then packing per
 * box) and put away on a shelf, a truckload at a time. Used by the Terrebonne
 * board and the order page.
 */

/** The finished-goods warehouse whose shelves boxes are put away on (NEXCA, at Terrebonne). */
export const FINISHED_GOODS_WAREHOUSE = "NEXCA";

type Translate = (key: string, fallback: string, options?: Record<string, unknown>) => string;

/** "Kit", "Foam", "Pack, box 2": a Terrebonne step as the floor says it. */
export function stepLabel(t: Translate, processName: string | null | undefined, boxNumber: number | null | undefined): string {
  const name = processName ?? "";
  return boxNumber == null ? name : t("assembly.step.box", "{{name}}, box {{box}}", { name, box: boxNumber });
}

/**
 * Puts what was packed since the last put-away on a shelf. The order completes
 * once nothing more can come: its production order closed, every truck
 * arrived, everything it has shelved. Free shelves first; one already holding
 * a lot is offered but flagged, since two lots can share one.
 */
export function PutAwayDialog({
  order,
  packed,
  onClose,
}: {
  order: ProductionOrderDTO;
  /** Units packed so far: the least packed box. */
  packed: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const assembly = useMemo(() => new AssemblyOrderClient(http), [http]);
  const locations = useQuery({
    queryKey: ["production", "storage-location", FINISHED_GOODS_WAREHOUSE],
    queryFn: () => new StorageLocationClient(http).forWarehouse(FINISHED_GOODS_WAREHOUSE),
  });
  const [chosen, setChosen] = useState(order.storage_location_id == null ? "" : String(order.storage_location_id));
  const [note, setNote] = useState("");
  const fresh = Math.max(0, packed - Number(order.quantity_put_away ?? 0));

  const putAway = useMutation({
    mutationFn: () => assembly.putAway(order.id, Number(chosen), note.trim() || null),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ["production"] });
      onClose();
      toast.success(
        updated.status === "COMPLETED"
          ? t("assembly.put-away.completed", "Order {{n}} completed: at {{location}}", { n: updated.number, location: updated.storage_location_code ?? "" })
          : t("assembly.put-away.done", "Put away at {{location}}", { location: updated.storage_location_code ?? "" }),
      );
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const options = (locations.data ?? [])
    .slice()
    .sort((a, b) => Number(a.production_order_id != null) - Number(b.production_order_id != null) || a.position - b.position)
    .map((location) => ({
      value: String(location.id),
      label:
        location.production_order_id != null && location.production_order_id !== order.id
          ? t("tb.location.taken", "{{code}} (lot {{n}} is there)", { code: location.code, n: location.production_order_number })
          : t("tb.location.free", "{{code}} (free)", { code: location.code }),
    }));

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("assembly.put-away.title", "Put away order {{n}}", { n: order.number })}
      description={t(
        "assembly.put-away.description",
        "{{packed}} of {{firmed}} packed. What was packed since the last put-away goes on this shelf; the order completes once everything that will come is shelved.",
        { packed: formatNumber(packed), firmed: formatNumber(order.firmed_quantity ?? 0) },
      )}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={chosen === "" || fresh <= 0} loading={putAway.isPending} onClick={() => putAway.mutate()}>
            {t("assembly.put-away.confirm", "Put away")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("packing.put-away.location", "Location")} htmlFor="assembly-location">
          <Select
            id="assembly-location"
            value={chosen}
            placeholder={t("packing.put-away.choose", "Choose a location")}
            options={options}
            onChange={(event) => setChosen(event.target.value)}
          />
        </Field>
        <Field label={t("form.field.note", "Note")} htmlFor="assembly-note">
          <Input id="assembly-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        {fresh > 0 && (
          <p className="text-[12px] text-ink-muted">{t("assembly.put-away.fresh", "{{n}} boxes to place", { n: formatNumber(fresh) })}</p>
        )}
      </div>
    </Dialog>
  );
}

/**
 * A production order's sets shared between its versions: each version's
 * share of the lot, what has arrived for it, and its assembly order. Any split
 * that adds up to the lot, until a version's assembly starts or the window
 * after the first sets arrive closes (production settings).
 */
export function SplitPanel({ fabricationOrderId, editable }: { fabricationOrderId: number; editable: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const assembly = useMemo(() => new AssemblyOrderClient(http), [http]);
  const split = useQuery({
    queryKey: ["production", "assembly-split", fabricationOrderId],
    queryFn: () => assembly.split(fabricationOrderId),
  });
  const [draft, setDraft] = useState<Record<number, string>>({});
  const data = split.data;
  const lines = data?.lines ?? [];
  const valueOf = (id: number, quantity: number) => draft[id] ?? String(Number(quantity));
  const total = lines.reduce((sum, line) => sum + Number(valueOf(line.assembly_order_id, line.quantity)), 0);
  const changed = lines.some((line) => Number(valueOf(line.assembly_order_id, line.quantity)) !== Number(line.quantity));
  const valid = data != null && total === Number(data.total) && lines.every((line) => {
    const value = Number(valueOf(line.assembly_order_id, line.quantity));
    return Number.isInteger(value) && value >= 0;
  });

  const save = useMutation({
    mutationFn: () =>
      assembly.resplit(
        fabricationOrderId,
        lines.map((line) => ({ assembly_order_id: line.assembly_order_id, quantity: Number(valueOf(line.assembly_order_id, line.quantity)) })),
      ),
    onSuccess: async () => {
      setDraft({});
      await queryClient.invalidateQueries({ queryKey: ["production"] });
      toast.success(t("assembly.split.saved", "Split saved"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  if (split.isLoading) {
    return <p className="text-[13px] text-ink-muted">{t("common.loading", "Loading…")}</p>;
  }
  if (!data || lines.length === 0) {
    return (
      <Card className="p-4 sm:p-5">
        <p className="text-[13px] text-ink-muted">
          {t("assembly.split.none", "This order has no assembly orders: its product has no assembly version.")}
        </p>
      </Card>
    );
  }
  const canEdit = editable && data.editable;

  return (
    <Card className="flex flex-col gap-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline gap-3">
        <h3 className="text-[13px] font-semibold">{t("assembly.split.title", "Assembly orders")}</h3>
        <span className="text-[12px] text-ink-muted">
          {t("assembly.split.received", "{{received}} of {{total}} sets received at Terrebonne", {
            received: formatNumber(data.received),
            total: formatNumber(data.total),
          })}
        </span>
        {data.editable && data.editable_until && (
          <Badge variant="warning" size="sm">
            {t("assembly.split.until", "Split open until {{when}}", { when: new Date(data.editable_until).toLocaleString() })}
          </Badge>
        )}
      </div>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[11px] text-ink-muted">
            <th className="pb-2 pr-3 font-medium">{t("mo.version", "Version")}</th>
            <th className="pb-2 pr-3 font-medium">{t("column.sku", "SKU")}</th>
            <th className="pb-2 pr-3 font-medium">{t("assembly.split.share", "Share of the lot")}</th>
            <th className="pb-2 pr-3 text-right font-medium">{t("assembly.split.firmed", "Arrived")}</th>
            <th className="pb-2 font-medium">{t("column.status", "Status")}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.assembly_order_id} className="border-t border-line">
              <td className="py-2 pr-3 font-medium">{line.version_code}</td>
              <td className="py-2 pr-3">
                <Link
                  to="/production/orders/$orderId"
                  params={{ orderId: String(line.assembly_order_id) }}
                  className="text-accent underline-offset-2 hover:underline"
                >
                  {line.output_sku}
                </Link>
                <span className="ml-2 text-[11px] tabular-nums text-ink-faint">{line.number}</span>
              </td>
              <td className="py-2 pr-3">
                {canEdit ? (
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    className="w-24"
                    aria-label={t("assembly.split.share", "Share of the lot")}
                    value={valueOf(line.assembly_order_id, line.quantity)}
                    onChange={(event) => setDraft({ ...draft, [line.assembly_order_id]: event.target.value })}
                  />
                ) : (
                  <span className="tabular-nums">{formatNumber(line.quantity)}</span>
                )}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">{formatNumber(line.firmed_quantity)}</td>
              <td className="py-2">
                <ProductionStatusBadge status={line.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit ? (
        <div className="flex items-center gap-3">
          <span className={total === Number(data.total) ? "text-[12px] text-ink-muted" : "text-[12px] text-warn"}>
            {t("assembly.split.total", "{{total}} of {{lot}}", { total: formatNumber(total), lot: formatNumber(data.total) })}
          </span>
          <Button size="sm" variant="primary" className="ml-auto" disabled={!changed || !valid} loading={save.isPending} onClick={() => save.mutate()}>
            {t("assembly.split.save", "Save the split")}
          </Button>
        </div>
      ) : (
        data.locked_reason && <p className="text-[12px] text-ink-muted">{data.locked_reason}</p>
      )}
    </Card>
  );
}
